import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

import { resolveHttpResourceUrl } from "../../lib/http/resourceUrl";

type MarkdownContentProps = {
  content: string;
  className?: string;
  highlightTerms?: readonly string[];
  highlightLabels?: ReadonlyMap<string, string>;
};

type MarkdownAstNode = {
  type: string;
  tagName?: string;
  value?: string;
  children?: MarkdownAstNode[];
  properties?: Record<string, unknown>;
};

/** 在 Markdown 渲染树中优先匹配更长的考察点名称，避免包含关系重复标记。 */
function createPointHighlightPlugin(highlightTerms: readonly string[], highlightLabels?: ReadonlyMap<string, string>) {
  const terms = [...new Set(highlightTerms.map((term) => term.trim()).filter(Boolean))]
    .sort((left, right) => Array.from(right).length - Array.from(left).length);

  return () => (tree: MarkdownAstNode) => {
    if (!terms.length) return;
    transformTextNodes(tree, terms, highlightLabels);
  };
}

/** 改写 rehype-raw 解析后的普通文本节点，代码与脚本内容保持原样。 */
function transformTextNodes(node: MarkdownAstNode, terms: readonly string[], labels?: ReadonlyMap<string, string>): void {
  if (!node.children || (node.type === "element" && ["code", "pre", "script", "style"].includes(node.tagName ?? ""))) return;
  const nextChildren: MarkdownAstNode[] = [];
  for (const child of node.children) {
    if (child.type === "text" && child.value) {
      nextChildren.push(...highlightText(child.value, terms, labels));
      continue;
    }
    transformTextNodes(child, terms, labels);
    nextChildren.push(child);
  }
  node.children = nextChildren;
}

/** 从左到右扫描文本，同一位置始终采用已经按长度降序排列的首个名称。 */
function highlightText(value: string, terms: readonly string[], labels?: ReadonlyMap<string, string>): MarkdownAstNode[] {
  const nodes: MarkdownAstNode[] = [];
  let plainText = "";
  let index = 0;

  const flushPlainText = () => {
    if (!plainText) return;
    nodes.push({ type: "text", value: plainText });
    plainText = "";
  };

  while (index < value.length) {
    const matched = terms.find((term) => value.startsWith(term, index));
    if (!matched) {
      plainText += value[index];
      index += 1;
      continue;
    }
    flushPlainText();
    nodes.push({
      type: "element",
      tagName: "strong",
      properties: { className: ["itinerary-point-emphasis"] },
      children: [{ type: "text", value: matched }],
    });
    const label = labels?.get(matched);
    if (label) nodes.push({ type: "text", value: `（${label}）` });
    index += matched.length;
  }
  flushPlainText();
  return nodes;
}

/** 只改写已知后端媒体资源，保留 Markdown 默认协议校验及前端导航地址。 */
function resolveMarkdownResourceUrl(value: string): string {
  const safeUrl = defaultUrlTransform(value);
  return /^\/(?:media\/local-files\/|suppliers\/media\/|itineraries\/public\/pdf-head-images\/)/.test(safeUrl)
    ? resolveHttpResourceUrl(safeUrl)
    : safeUrl;
}

/** 统一渲染 Markdown 描述，并将历史 HTML 换行纳入普通换行规则。 */
export function MarkdownContent({ content, className, highlightTerms = [], highlightLabels }: MarkdownContentProps) {
  const classes = ["itinerary-markdown", className].filter(Boolean).join(" ");
  const normalized = content
    .trim()
    .replace(/^(?:<br\s*\/?\s*>\s*)+/gi, "")
    .replace(/(?:\s*<br\s*\/?\s*>)+$/gi, "")
    .trim()
    .replace(/<br\s*\/?\s*>/gi, "\n");
  const pointHighlightPlugin = createPointHighlightPlugin(highlightTerms, highlightLabels);

  return (
    <div className={classes}>
      <ReactMarkdown
        urlTransform={resolveMarkdownResourceUrl}
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[rehypeRaw, rehypeSanitize, pointHighlightPlugin]}
      >
        {normalized}
      </ReactMarkdown>
    </div>
  );
}
